// persistStorage.js — v0.177.0 — asking the browser not to throw the site's
// photos away.
// ---------------------------------------------------------------------------
// 2026-09-15, after McDonald's Warnbro. Site mode means a day's photos can sit
// in IndexedDB for hours before they are uploaded. That storage is BEST-EFFORT
// by default: iOS clears a web app's data after roughly seven days of not
// being used, and any browser may evict it under storage pressure. The device
// is under no obligation to keep a single one of those photos.
//
// The Storage API lets a site ask for its data to be treated as PERSISTENT,
// which exempts it from that eviction. Browsers decide by their own heuristics
// — Safari grants it to an app added to the Home Screen; Chrome weighs
// engagement and installation — so the request can be refused, and the answer
// is worth KNOWING either way rather than assuming.
//
// This module asks once per load, remembers the answer, and reports it. It
// never blocks anything: a refusal is not an error, it is a fact the sync
// strip can show ("add DropPin to your Home Screen to protect photos held on
// this device").
//
// Nothing here touches the queue. It cannot lose data; at worst it changes
// nothing.

const _state = {
  asked: false,
  persisted: null,    // true | false | null (unknown / unsupported)
  quota: null,        // bytes the origin may use, when the browser says
  usage: null,        // bytes in use
};

const _listeners = new Set();
export function subscribeStorage(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}
function notify() { _listeners.forEach((fn) => { try { fn(getStorageState()); } catch (_e) { /* listener's problem */ } }); }

export function getStorageState() { return { ..._state }; }

function api() {
  return (typeof navigator !== 'undefined' && navigator.storage) ? navigator.storage : null;
}

/**
 * Ask the browser to keep this origin's data. Safe to call on every load —
 * `persisted()` short-circuits once it has already been granted, and a refusal
 * is never retried in a loop.
 *
 * Returns the state, never throws.
 */
export async function requestPersistentStorage() {
  const s = api();
  if (!s || typeof s.persist !== 'function') {
    _state.asked = true;
    _state.persisted = null;    // unsupported: unknown, not "refused"
    notify();
    return getStorageState();
  }
  try {
    const already = typeof s.persisted === 'function' ? await s.persisted() : false;
    _state.persisted = already ? true : await s.persist();
  } catch (_e) {
    _state.persisted = null;
  }
  _state.asked = true;
  await refreshEstimate();
  notify();
  return getStorageState();
}

/** How much room the queue has, when the browser will say. Never throws. */
export async function refreshEstimate() {
  const s = api();
  if (!s || typeof s.estimate !== 'function') return getStorageState();
  try {
    const e = await s.estimate();
    _state.quota = Number.isFinite(e && e.quota) ? e.quota : null;
    _state.usage = Number.isFinite(e && e.usage) ? e.usage : null;
  } catch (_e) { /* leave whatever we had */ }
  return getStorageState();
}

// ── Pure: what the user should be told ────────────────────────────────
//
// Only worth saying something when the app is HOLDING work. With an empty
// queue, storage that may be evicted costs nothing and a warning is noise.
//
//   state    from getStorageState()
//   pending  how many changes are waiting on this device
//
// → null, or { tone: 'warn' | 'info', text }
export function storageAdvice(state, pending) {
  const held = Number(pending) || 0;
  if (held <= 0) return null;
  if (state && state.persisted === true) return null;

  const isIos = typeof navigator !== 'undefined'
    && /iPad|iPhone|iPod/.test(navigator.userAgent || '')
    // iPadOS 13+ reports as a Mac; the touch points give it away.
    || (typeof navigator !== 'undefined' && /Macintosh/.test(navigator.userAgent || '') && Number(navigator.maxTouchPoints) > 1);

  const where = isIos
    ? 'Add DropPin to your Home Screen (Share → Add to Home Screen) and iOS will stop clearing it.'
    : 'Install DropPin (the Install button in the header) and the browser will stop clearing it.';

  return {
    tone: 'warn',
    text: `${held} change${held === 1 ? '' : 's'} ${held === 1 ? 'is' : 'are'} held on this device, and this browser has not promised to keep them. ${where} Or sync now.`,
  };
}

/** True when the queue is within sight of the storage the browser allows. */
export function storageIsTight(state, headroomBytes = 25 * 1024 * 1024) {
  if (!state || !Number.isFinite(state.quota) || !Number.isFinite(state.usage)) return false;
  return (state.quota - state.usage) < headroomBytes;
}
